import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatMinutes, isoDate } from '../lib/format';
import { MAX_OVERTIME_MINUTES, type Overtime, type OvertimeDay, type OvertimeStatus } from '../lib/overtime';
import { FormGrid, FormPage } from '../components/FormPage';
import { Detail, Field, Input, Select, Textarea, useToast } from '../components/ui';
import { dayKindLabel } from './OvertimeDetail';

interface Pickable {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  department: { id: string; name: string; nameAr: string | null } | null;
}

interface Preview {
  day: OvertimeDay;
  existing: { id: string; status: OvertimeStatus; minutes: number } | null;
  monthSignedOff: boolean;
}

/**
 * Route: `/overtime/new` — a department's leader sends overtime for one of their
 * people on one day, seeing the day first: what it owes and, once over, what was
 * worked beyond it. An administrator sends it for anyone, approved at once.
 * `?userId=` and `?date=` fill it in.
 */
export function OvertimeFormPage() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    userId: params.get('userId') ?? '',
    workDate: params.get('date') ?? isoDate(),
    hours: '1',
    minutes: '0',
    reason: '',
  });
  const approver = can(P.OVERTIME_APPROVE);

  const people = useQuery({
    queryKey: ['overtime', 'people'],
    queryFn: () => api.get<Pickable[]>('/overtime/people'),
  });

  const ready = !!form.userId && /^\d{4}-\d{2}-\d{2}$/.test(form.workDate);
  const preview = useQuery({
    queryKey: ['overtime', 'preview', form.userId, form.workDate],
    queryFn: () => api.get<Preview>('/overtime/preview', { userId: form.userId, workDate: form.workDate }),
    enabled: ready,
  });

  const total = (Number(form.hours) || 0) * 60 + (Number(form.minutes) || 0);
  const totalOk = Number.isInteger(total) && total >= 1 && total <= MAX_OVERTIME_MINUTES;

  const save = useMutation({
    mutationFn: () =>
      api.post<Overtime>('/overtime', {
        userId: form.userId,
        workDate: form.workDate,
        minutes: total,
        reason: form.reason.trim(),
      }),
    onSuccess: (created) => {
      toast.success(
        created.status === 'APPROVED'
          ? t('overtime.sentToEmployee', { name: localized(created.user) })
          : t('overtime.sentForApproval'),
      );
      void queryClient.invalidateQueries({ queryKey: ['overtime'] });
      navigate(`/overtime/${created.id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const p = preview.data;
  const blocked = !!p?.existing || !!p?.monthSignedOff;
  const picked = people.data?.find((u) => u.id === form.userId);

  return (
    <FormPage
      title={t('overtime.new')}
      subtitle={t(approver ? 'overtime.sendNoteApprover' : 'overtime.sendNote')}
      backTo="/overtime?tab=team"
      backLabel={t('overtime.title')}
      submitLabel={t('overtime.send')}
      submitting={save.isPending}
      disabled={!ready || !totalOk || !form.reason.trim() || blocked || preview.isLoading}
      loading={people.isLoading}
      error={people.error ?? (people.isSuccess && people.data.length === 0 ? new Error(t('overtime.noPeople')) : null)}
      onRetry={() => void people.refetch()}
      onSubmit={() => save.mutate()}
      summary={
        ready && (
          <dl>
            {picked && <Detail label={t('common.employee')}>{localized(picked)}</Detail>}
            <Detail label={t('overtime.day')}>
              <span className="tabular">{formatDate(form.workDate, locale)}</span>
            </Detail>
            {preview.isLoading ? (
              <Detail label={t('overtime.dayIs')}>…</Detail>
            ) : preview.error ? (
              <p className="pt-1 text-body-sm text-error-fg dark:text-error-dark">{(preview.error as Error).message}</p>
            ) : (
              p && (
                <>
                  <Detail label={t('overtime.dayIs')}>{dayKindLabel(p.day, t, localized)}</Detail>
                  <Detail label={t('overtime.owes')}>
                    <span className="tabular">{p.day.owedMinutes == null ? '—' : formatMinutes(p.day.owedMinutes, locale)}</span>
                  </Detail>
                  {p.day.judged && (
                    <>
                      <Detail label={t('overtime.worked')}>
                        <span className="tabular">{formatMinutes(p.day.workedMinutes, locale)}</span>
                      </Detail>
                      <Detail label={t('overtime.beyond')}>
                        <span className="tabular">{formatMinutes(p.day.beyondMinutes, locale)}</span>
                      </Detail>
                    </>
                  )}
                  {p.existing && (
                    <p className="pt-1 text-body-sm text-warning-fg dark:text-warning-dark">
                      {t('overtime.exists', {
                        time: formatMinutes(p.existing.minutes, locale),
                        status: t(`overtime.status.${p.existing.status}` as TranslationKey),
                      })}
                    </p>
                  )}
                  {p.monthSignedOff && (
                    <p className="pt-1 text-body-sm text-warning-fg dark:text-warning-dark">{t('overtime.monthSignedOff')}</p>
                  )}
                </>
              )
            )}
          </dl>
        )
      }
    >
      <FormGrid>
        <Field label={t('common.employee')} required>
          <Select value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })} autoFocus>
            <option value="">—</option>
            {people.data?.map((u) => (
              <option key={u.id} value={u.id}>
                {localized(u)}
                {u.department ? ` — ${localized(u.department)}` : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('overtime.day')} required hint={t('overtime.dayHint')}>
          <Input type="date" value={form.workDate} onChange={(e) => setForm({ ...form, workDate: e.target.value })} />
        </Field>
        <Field label={t('overtime.hours')} required>
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            max={12}
            step={1}
            value={form.hours}
            onChange={(e) => setForm({ ...form, hours: e.target.value })}
          />
        </Field>
        <Field label={t('overtime.minutes')} hint={totalOk ? t('overtime.upToHint', { time: formatMinutes(total, locale) }) : t('overtime.durationRange')}>
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            max={59}
            step={5}
            value={form.minutes}
            onChange={(e) => setForm({ ...form, minutes: e.target.value })}
          />
        </Field>
        <Field label={t('common.reason')} required hint={t('overtime.reasonHint')} className="sm:col-span-2">
          <Textarea value={form.reason} maxLength={1000} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/** Routes: `/overtime/:id/reject` and `/overtime/:id/decline` — whoever sent it reads why. A reason is required to reject. */
export function OvertimeAnswerPage({ action }: { action: 'reject' | 'decline' }) {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const overtime = useQuery({
    queryKey: ['overtime', 'one', id],
    queryFn: () => api.get<Overtime>(`/overtime/${id}`),
  });

  const send = useMutation({
    mutationFn: () => api.post(`/overtime/${id}/${action}`, { note: note.trim() || undefined }),
    onSuccess: () => {
      toast.success(t(action === 'reject' ? 'overtime.rejectedToast' : 'overtime.declinedToast'));
      void queryClient.invalidateQueries({ queryKey: ['overtime'] });
      // Back to the list or the overtime's page, whichever it came from.
      if (location.key !== 'default') navigate(-1);
      else navigate(`/overtime/${id}`, { replace: true });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const r = overtime.data;
  const allowed = r ? (action === 'reject' ? r.can.decide : r.can.answer) : false;

  return (
    <FormPage
      title={t(action === 'reject' ? 'overtime.rejectTitle' : 'overtime.declineTitle')}
      backLabel={t('common.back')}
      submitLabel={t(action === 'reject' ? 'common.reject' : 'overtime.decline')}
      destructive
      submitting={send.isPending}
      disabled={(action === 'reject' && !note.trim()) || (!!r && !allowed)}
      loading={overtime.isLoading}
      error={overtime.error}
      onRetry={() => void overtime.refetch()}
      onSubmit={() => send.mutate()}
      summary={
        r && (
          <dl>
            <Detail label={t('common.employee')}>{localized(r.user)}</Detail>
            <Detail label={t('overtime.day')}>
              <span className="tabular">{formatDate(r.workDate, locale)}</span>
            </Detail>
            <Detail label={t('overtime.upTo')}>
              <span className="tabular">{formatMinutes(r.minutes, locale)}</span>
            </Detail>
            {r.requestedBy && <Detail label={t('overtime.sentBy')}>{localized(r.requestedBy)}</Detail>}
            <Detail label={t('common.reason')}>{r.reason}</Detail>
          </dl>
        )
      }
    >
      <Field label={t(action === 'reject' ? 'overtime.rejectReason' : 'overtime.declineReason')} required={action === 'reject'}>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} autoFocus />
      </Field>
    </FormPage>
  );
}
